import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Form, Modal } from 'react-bootstrap';
import { FaCog } from 'react-icons/fa';
import adminSchemaApi from '../api/adminSchemaApi';
import { applyTelephonyPackageToggle, normalizeAllowedObjects, normalizePlanConfig, STANDARD_OBJECT_OPTIONS } from './AdminPlanConfigConstants';
import PlanConfigForm from './PlanConfigForm';
import '../styles/admin-theme.css';

/** Deep merge: base + overrides (overrides win). */
function deepMerge(base, overrides) {
  if (!overrides || typeof overrides !== 'object') return base ? JSON.parse(JSON.stringify(base)) : {};
  const out = base ? JSON.parse(JSON.stringify(base)) : {};
  Object.keys(overrides).forEach((k) => {
    if (overrides[k] != null && typeof overrides[k] === 'object' && !Array.isArray(overrides[k])) {
      out[k] = deepMerge(out[k] || {}, overrides[k]);
    } else {
      out[k] = overrides[k];
    }
  });
  return out;
}

const SubscriptionConfigModal = ({ show, onHide, companyId, subscription, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [planConfig, setPlanConfig] = useState(null);
  const [config, setConfig] = useState({});
  const [standardObjects, setStandardObjects] = useState([]);
  const [standardTabs, setStandardTabs] = useState([]);

  useEffect(() => {
    if (!show || !subscription?.plan_id) return;
    setError('');
    setLoading(true);
    adminSchemaApi
      .fetchPlanById(subscription.plan_id)
      .then(async (plan) => {
        const base = normalizePlanConfig(plan?.config || {});
        setPlanConfig(base);
        const overrides = subscription.config && typeof subscription.config === 'object' ? subscription.config : {};
        setConfig(deepMerge(base, overrides));
        const packages = overrides.enabledPackages ?? base.enabledPackages ?? [];
        const [objects, tabs] = await Promise.all([
          adminSchemaApi.fetchStandardObjects(packages).catch(() => []),
          adminSchemaApi.fetchStandardTabs(packages).catch(() => []),
        ]);
        setStandardObjects(Array.isArray(objects) ? objects : []);
        setStandardTabs(Array.isArray(tabs) ? tabs : []);
      })
      .catch(() => setError('Failed to load plan config'))
      .finally(() => setLoading(false));
  }, [show, subscription?.plan_id, subscription?.config]);

  const handlePackageToggle = useCallback(async (packageKey, checked) => {
    let next = applyTelephonyPackageToggle(config.enabledPackages ?? [], packageKey, checked);
    if (checked && packageKey === 'email_campaign') {
      next = [...new Set([...next, 'watconnect'])];
    }
    if (checked) {
      const packageTabIdsToAdd = (standardTabs || [])
        .filter((t) => String(t.config?.package || '').trim() === packageKey)
        .map((t) => t.id)
        .filter(Boolean);
      setConfig((prev) => ({
        ...prev,
        enabledPackages: next,
        ...(packageKey === 'email_campaign'
          ? {
              allowedObjectsPermission: [...new Set([...(prev.allowedObjectsPermission ?? []), 'email_campaign', 'email_campaign_member'])],
              objectManager: {
                ...prev.objectManager,
                allowedObjects: [...new Set([...(prev.objectManager?.allowedObjects ?? []), 'email_campaign', 'email_campaign_member'])],
              },
            }
          : {}),
        ...(packageTabIdsToAdd.length > 0
          ? { tab: { ...prev.tab, allowedTabs: [...new Set([...(prev.tab?.allowedTabs ?? []), ...packageTabIdsToAdd])] } }
          : {}),
      }));
    } else if (!checked) {
      setConfig((prev) => {
        const packageTabIdsToRemove = new Set(
          (standardTabs || []).filter((t) => String(t.config?.package || '').trim() === packageKey).map((t) => t.id).filter(Boolean)
        );
        const allowedTabs = (prev.tab?.allowedTabs ?? []).filter((id) => !packageTabIdsToRemove.has(id));
        const updates = {
          ...prev,
          enabledPackages: next,
          tab: { ...prev.tab, allowedTabs },
        };
        if (packageKey === 'email_campaign') {
          updates.allowedObjectsPermission = (prev.allowedObjectsPermission ?? []).filter((o) => o !== 'email_campaign' && o !== 'email_campaign_member');
          updates.objectManager = {
            ...prev.objectManager,
            allowedObjects: (prev.objectManager?.allowedObjects ?? []).filter((o) => o !== 'email_campaign' && o !== 'email_campaign_member'),
          };
        }
        return updates;
      });
    } else {
      setConfig((prev) => ({ ...prev, enabledPackages: next }));
    }
    const [objects, tabs] = await Promise.all([
      adminSchemaApi.fetchStandardObjects(next).catch(() => []),
      adminSchemaApi.fetchStandardTabs(next).catch(() => []),
    ]);
    setStandardObjects(Array.isArray(objects) ? objects : []);
    setStandardTabs(Array.isArray(tabs) ? tabs : []);
    if (checked && Array.isArray(tabs) && tabs.length > 0) {
      const packageTabIdsToAdd = tabs
        .filter((t) => String(t.config?.package || '').trim() === packageKey)
        .map((t) => t.id)
        .filter(Boolean);
      if (packageTabIdsToAdd.length > 0) {
        setConfig((prev) => ({
          ...prev,
          tab: { ...prev.tab, allowedTabs: [...new Set([...(prev.tab?.allowedTabs ?? []), ...packageTabIdsToAdd])] },
        }));
      }
    }
  }, [config.enabledPackages, standardTabs]);

  const standardPicklistOptions = useMemo(() => {
    if (standardObjects.length > 0) {
      return standardObjects.map((o) => {
        const name = o.object_name || '';
        const label = name.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
        return { key: name, value: label || name };
      });
    }
    return STANDARD_OBJECT_OPTIONS.map((o) => ({ key: o.name, value: o.label }));
  }, [standardObjects]);

  const leadConversionPicklistOptions = useMemo(() => {
    const allowed = config.allowedObjectsPermission ?? [];
    return standardPicklistOptions.filter((o) => allowed.includes(o.key));
  }, [standardPicklistOptions, config.allowedObjectsPermission]);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!companyId || !subscription?.id) return;
    setSaving(true);
    setError('');
    try {
      const toSave = {
        ...config,
        allowedObjectsPermission: normalizeAllowedObjects(config.allowedObjectsPermission ?? []),
        objectManager: {
          ...config.objectManager,
          allowedObjects: normalizeAllowedObjects(config.objectManager?.allowedObjects ?? []),
        },
      };
      await adminSchemaApi.updateSubscription(companyId, subscription.id, { config: toSave });
      onHide();
      if (typeof onSuccess === 'function') onSuccess();
    } catch (err) {
      setError(err?.message || 'Failed to save config');
    } finally {
      setSaving(false);
    }
  };

  if (!subscription) return null;

  return (
    <Modal show={show} onHide={onHide} centered size="xl">
      <Modal.Header closeButton>
        <Modal.Title className="admin-title">
          <FaCog className="me-2" />
          Override config — {subscription.name || subscription.id}
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSave}>
        <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {error && (
            <Alert variant="danger" dismissible onClose={() => setError('')}>
              {error}
            </Alert>
          )}
          <p className="text-muted small mb-3">
            Override plan config for this subscription. Same structure as plan config. Values here replace the plan defaults.
          </p>
          {loading ? (
            <p className="admin-subtitle">Loading…</p>
          ) : planConfig ? (
            <PlanConfigForm
              config={config}
              onChange={setConfig}
              disabled={saving}
              standardPicklistOptions={standardPicklistOptions}
              leadConversionPicklistOptions={leadConversionPicklistOptions}
              onPackageToggle={handlePackageToggle}
              standardTabs={standardTabs}
            />
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={onHide} disabled={saving}>
            Cancel
          </Button>
          <Button className="admin-primary-btn" type="submit" disabled={saving || loading}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default SubscriptionConfigModal;
